/**
 * Ruby Token units + vibe calendar helpers, from @platform/shared-types.
 *
 * GUARDED at module scope (feedback_shared_types_runtime_import_guard): these
 * are RUNTIME values from the linked shared-types, and a Vite pre-bundle
 * built before they existed hands back `undefined` — which would white-screen
 * the whole AI Control Center. Each one falls back to a local equivalent and
 * logs the real remedy (RESTART the dev server with `vite --force`).
 *
 * Unit rule (D7): 1 stored credit = 1,000 Ruby Tokens. Never write "RT".
 */
import {
  RUBY_TOKENS_PER_CREDIT as ST_PER_CREDIT,
  creditsToRubyTokens as stCreditsToRubyTokens,
  formatRubyTokens as stFormatRubyTokens,
  formatRubyTokensExact as stFormatRubyTokensExact,
  isVibeInSeason as stIsVibeInSeason,
  isVibeNew as stIsVibeNew,
  AI_MODEL_USED_FOR as ST_USED_FOR,
  setDisplayTokensPerCredit as stSetDisplayTokensPerCredit,
} from '@platform/shared-types';

const stale = (name: string) => console.error(
  `@platform/shared-types exported no ${name} — a stale Vite pre-bundle. RESTART the dev server (npx vite --force): deleting node_modules/.vite under a running server does nothing.`,
);

function guard<T>(name: string, value: T | undefined, ok: (v: unknown) => boolean, fallback: T): T {
  if (ok(value)) return value as T;
  stale(name);
  return fallback;
}

const isFn = (v: unknown) => typeof v === 'function';

export const RUBY_TOKENS_PER_CREDIT: number = guard(
  'RUBY_TOKENS_PER_CREDIT', ST_PER_CREDIT, (v) => typeof v === 'number' && v > 0, 1000,
);

// ---------- live Ruby Token value (owner 2026-10-17) ----------
// "1 Ruby Token = $X" and the default multiplier live on the AI registry
// CONFIG row (AI → Settings). AiControlCenter applies them on every registry
// load; until then — or if the row is missing — the defaults below hold.
const DEFAULT_DEFAULT_MULTIPLIER = 1.5;
let liveRate = RUBY_TOKENS_PER_CREDIT;
let liveMultiplier = DEFAULT_DEFAULT_MULTIPLIER;

/** Apply the registry's live values; null restores the defaults. Invalid values are ignored. */
export function setLiveEconomics(e: { tokensPerCredit?: number; defaultMultiplier?: number } | null | undefined): void {
  const per = e?.tokensPerCredit;
  liveRate = typeof per === 'number' && Number.isInteger(per) && per >= 1 ? per : RUBY_TOKENS_PER_CREDIT;
  const m = e?.defaultMultiplier;
  liveMultiplier = typeof m === 'number' && Number.isFinite(m) && m > 0 && m <= 50 ? m : DEFAULT_DEFAULT_MULTIPLIER;
  // Every shared-types display helper (formatCreditsAsRubyTokens…) follows too.
  if (typeof stSetDisplayTokensPerCredit === 'function') stSetDisplayTokensPerCredit(liveRate);
}

/** Live Ruby Tokens per stored credit (default 1,000). */
export const liveTokensPerCredit = (): number => liveRate;
/** Live Ruby Tokens per $1 (default 100,000). */
export const liveTokensPerUsd = (): number => liveRate * 100;
/** Live default multiplier (default 1.5). */
export const liveDefaultMultiplier = (): number => liveMultiplier;

const stCredits = guard('creditsToRubyTokens', stCreditsToRubyTokens, isFn, null as unknown as typeof stCreditsToRubyTokens);
/** Stored credits → Ruby Tokens at the live rate. */
export const creditsToRubyTokens = (credits: number): number =>
  stCredits ? stCredits(credits, liveRate) : (Number.isFinite(credits) ? Math.round(credits * liveRate) : 0);

export const formatRubyTokens: (tokens: number) => string = guard(
  'formatRubyTokens', stFormatRubyTokens, isFn,
  (t: number) => (Number.isFinite(t) ? Math.round(t).toLocaleString('en-US') : '0'),
);

export const formatRubyTokensExact: (tokens: number) => string = guard(
  'formatRubyTokensExact', stFormatRubyTokensExact, isFn,
  (t: number) => (Number.isFinite(t) ? Math.round(t).toLocaleString('en-US') : '0'),
);

type Season = { fromMonthDay: string; toMonthDay: string } | null | undefined;

export const isVibeInSeason: (season: Season, date?: Date) => boolean = guard(
  'isVibeInSeason', stIsVibeInSeason as any, isFn,
  (season: Season, date: Date = new Date()) => {
    if (!season) return true;
    const md = `${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    const { fromMonthDay: f, toMonthDay: t } = season;
    return f <= t ? md >= f && md <= t : md >= f || md <= t;
  },
);

export const isVibeNew: (
  v: { announceNewDays?: number; announcedAt?: string; createdAt?: string }, now?: Date,
) => boolean = guard('isVibeNew', stIsVibeNew as any, isFn, () => false);

export const AI_MODEL_USED_FOR: readonly string[] = guard(
  'AI_MODEL_USED_FOR', ST_USED_FOR as readonly string[] | undefined, Array.isArray,
  ['text', 'web_search', 'image', 'image_edit', 'video', 'voice', 'long_form'],
);

/** Stored credits → "1.37M Ruby Tokens". */
export const creditsLabel = (credits: number): string =>
  `${formatRubyTokens(creditsToRubyTokens(credits))} Ruby Tokens`;

/** Ruby Tokens → "6K Ruby Tokens" (singular for exactly 1). */
export const tokensLabel = (tokens: number): string =>
  `${formatRubyTokens(tokens)} Ruby Token${tokens === 1 ? '' : 's'}`;

export const usd = (n: number, digits = 2): string => {
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n);
  // Vendor costs per run are fractions of a cent — show enough digits to be true.
  const d = abs > 0 && abs < 0.01 ? 4 : digits;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(d)}`;
};
